// src/views/Reports/GraduationStatisticsReport.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";
import { Select } from "antd";
import toast from "react-hot-toast";
import { getGraduationStatistics } from "../../services/graduationStatisticsService";
import { getInstitutions } from "../../services/institutionService";
import { getSessions } from "../../services/sessionService";
import DownloadButton from "../../components/Buttons/DownloadButton";
import { generatePDF } from "../../utils/pdf";
import { utils, writeFile } from "xlsx";

const { Option } = Select;

const GraduationStatisticsReport = () => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedSession, setSelectedSession] = useState("Not Selected");
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, instRes, sessRes] = await Promise.all([
        getGraduationStatistics(),
        getInstitutions(),
        getSessions(),
      ]);
      const statData = statsRes?.data?.data || [];
      setStats(statData);
      setFilteredStats(statData);
      setInstitutions(instRes?.data?.data || []);
      setSessions(sessRes?.data?.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Filters
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        (item.institution?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution.toString()
      );
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, selectedInstitution, selectedSession, stats]);

  // Group by Institution + Session with totals
  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of filteredStats) {
      const inst = row.institution;
      const sess = row.session;
      if (!inst || !sess) continue;

      const key = `${inst.id}__${sess.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          institution: inst,
          session: sess,
          data: [],
          totals: {
            humanities_male: 0,
            humanities_female: 0,
            sciences_male: 0,
            sciences_female: 0,
            total: 0,
          },
        });
      }
      const g = map.get(key);
      g.data.push(row);

      const hm = Number(row.humanities_male_count || 0);
      const hf = Number(row.humanities_female_count || 0);
      const sm = Number(row.sciences_male_count || 0);
      const sf = Number(row.sciences_female_count || 0);

      g.totals.humanities_male += hm;
      g.totals.humanities_female += hf;
      g.totals.sciences_male += sm;
      g.totals.sciences_female += sf;
      g.totals.total += hm + hf + sm + sf;
    }
    return Array.from(map.values());
  }, [filteredStats]);

  const columns = [
    {
      title: "Institution",
      dataIndex: "institution",
      render: (institution) => (
        <span className="font-medium text-gray-800">{institution?.name}</span>
      ),
    },
    { title: "Year", dataIndex: ["session", "name"] },
    { title: "Male (Humanities)", dataIndex: ["totals", "humanities_male"] },
    {
      title: "Female (Humanities)",
      dataIndex: ["totals", "humanities_female"],
    },
    { title: "Male (Sciences)", dataIndex: ["totals", "sciences_male"] },
    { title: "Female (Sciences)", dataIndex: ["totals", "sciences_female"] },
    { title: "Total", dataIndex: ["totals", "total"] },
  ];

  const handleDownloadPDF = () => {
    const title = "Graduation Statistics Report";
    const fileName = "graduation-statistics-report.pdf";

    const columns = [
      "Institution / Year / Level",
      "Male (Humanities)",
      "Female (Humanities)",
      "Male (Sciences)",
      "Female (Sciences)",
      "Total",
    ];

    const rows = [];

    grouped.forEach((group) => {
      // Group summary row
      rows.push([
        `${group.institution?.name || "N/A"} — ${
          group.session?.name || "N/A"
        } (TOTAL)`,
        group.totals.humanities_male,
        group.totals.humanities_female,
        group.totals.sciences_male,
        group.totals.sciences_female,
        group.totals.total,
      ]);

      // Detail rows
      group.data.forEach((item) => {
        const hm = Number(item.humanities_male_count ?? 0);
        const hf = Number(item.humanities_female_count ?? 0);
        const sm = Number(item.sciences_male_count ?? 0);
        const sf = Number(item.sciences_female_count ?? 0);
        rows.push([
          `-> ${item.academic_program_level?.name || "N/A"}`,
          hm,
          hf,
          sm,
          sf,
          hm + hf + sm + sf,
        ]);
      });

      // Spacer
      rows.push(["", "", "", "", "", ""]);
    });

    generatePDF({ title, columns, rows, fileName });
  };

  // Excel export
  const handleDownloadExcel = () => {
    // --- Summary sheet (totals per Institution+Year) ---
    const summaryCols = [
      "Institution",
      "Year",
      "Male (Humanities)",
      "Female (Humanities)",
      "Male (Sciences)",
      "Female (Sciences)",
      "Total",
    ];

    const summaryRows = grouped.map((g) => [
      g.institution?.name || "N/A",
      g.session?.name || "N/A",
      g.totals.humanities_male,
      g.totals.humanities_female,
      g.totals.sciences_male,
      g.totals.sciences_female,
      g.totals.total,
    ]);

    // --- Details sheet (each program level row) ---
    const detailsCols = [
      "Institution",
      "Year",
      "Program Level",
      "Male (Humanities)",
      "Female (Humanities)",
      "Male (Sciences)",
      "Female (Sciences)",
      "Total",
    ];

    const detailsRows = [];
    grouped.forEach((g) => {
      g.data.forEach((item) => {
        const hm = Number(item.humanities_male_count ?? 0);
        const hf = Number(item.humanities_female_count ?? 0);
        const sm = Number(item.sciences_male_count ?? 0);
        const sf = Number(item.sciences_female_count ?? 0);

        detailsRows.push([
          g.institution?.name || "N/A",
          g.session?.name || "N/A",
          item.academic_program_level?.name || "N/A",
          hm,
          hf,
          sm,
          sf,
          hm + hf + sm + sf,
        ]);
      });
    });

    // Build workbook
    const wb = utils.book_new();

    const wsSummary = utils.aoa_to_sheet([summaryCols, ...summaryRows]);
    utils.book_append_sheet(wb, wsSummary, "Summary");

    const wsDetails = utils.aoa_to_sheet([detailsCols, ...detailsRows]);
    utils.book_append_sheet(wb, wsDetails, "Details");

    writeFile(wb, "graduation-statistics-report.xlsx");
  };

  return (
    <>
      <PageHeading title="Graduation Report" subtitle1="Reports" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Graduation Statistics Report"
            count={grouped.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchAdd={false}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={grouped.map((g) => ({
              key: g.key,
              institution: g.institution,
              session: g.session,
              totals: g.totals,
              data: g.data,
            }))}
            expandable={{
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: (expanded, record) => {
                setExpandedRowKey(expanded ? record.key : null);
              },
              expandedRowRender: (record) => (
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Program Level</th>
                      <th className="text-left p-2">Male (Humanities)</th>
                      <th className="text-left p-2">Female (Humanities)</th>
                      <th className="text-left p-2">Male (Sciences)</th>
                      <th className="text-left p-2">Female (Sciences)</th>
                      <th className="text-left p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-50">
                    {record.data.map((item) => {
                      const hm = Number(item.humanities_male_count ?? 0);
                      const hf = Number(item.humanities_female_count ?? 0);
                      const sm = Number(item.sciences_male_count ?? 0);
                      const sf = Number(item.sciences_female_count ?? 0);
                      return (
                        <tr key={item.id}>
                          <td className="p-2">
                            {item.academic_program_level?.name || "N/A"}
                          </td>
                          <td className="p-2">{hm}</td>
                          <td className="p-2">{hf}</td>
                          <td className="p-2">{sm}</td>
                          <td className="p-2">{sf}</td>
                          <td className="p-2">{hm + hf + sm + sf}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ),
            }}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedInstitution(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="Not Selected">Filter by Institution</Option>
                  {institutions.map((inst) => (
                    <Option key={inst.id} value={inst.id}>
                      {inst.name}
                    </Option>
                  ))}
                </Select>

                <Select
                  defaultValue={selectedSession}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedSession(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="Not Selected">Filter by Year</Option>
                  {sessions.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </div>
            }
          />

          <div className="mt-4 flex gap-2 flex-wrap">
            <DownloadButton
              onDownload={handleDownloadPDF}
              title="Download PDF"
            />
            <DownloadButton
              onDownload={handleDownloadExcel}
              title="Download Excel"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GraduationStatisticsReport;
